// Import single functions, not the 'semver' index, to keep the client bundle small
import valid from 'semver/functions/valid';
import lt from 'semver/functions/lt';
import { useMfeVersionManager } from '../use-mfe-version-manager';

// scheduling-ui 2.6.0 is the first release that shares React's JSX runtimes and
// react-dom/server with the host (HSAP-2885); older builds crash on React 19.
export const SCHEDULING_UI_REACT_SHARING_VERSION = '2.6.0';

// Track unparseable values that have already been warned about
const warnedUnparseableVersions = new Set<string>();

/**
 * Returns true only for a valid semver (leading 'v' allowed) < 2.6.0.
 * 'current', null, undefined, and versions >= 2.6.0 return false.
 * Unparseable non-'current' values return false and console.warn (once per distinct value).
 *
 * @param version The version string to check
 * @returns true if the version is a valid legacy version (< 2.6.0), false otherwise
 */
export function isLegacySchedulingUiVersion(
  version: string | null | undefined
): boolean {
  // null, undefined, or 'current' are not legacy versions
  if (!version || version === 'current') {
    return false;
  }

  // Parse the version (semver.valid returns the cleaned version or null)
  const parsedVersion = valid(version);

  // If the version is unparseable, warn once per distinct value and return false
  if (!parsedVersion) {
    if (!warnedUnparseableVersions.has(version)) {
      warnedUnparseableVersions.add(version);
      // eslint-disable-next-line no-console
      console.warn(
        `scheduling-ui version warning: Unparseable version '${version}' is not a legacy version`
      );
    }
    return false;
  }

  // Return true if the parsed version is less than the boundary version
  return lt(parsedVersion, SCHEDULING_UI_REACT_SHARING_VERSION);
}

/**
 * React hook that returns null until the version manager is available,
 * then returns whether the current scheduling-ui version is a legacy version.
 *
 * @returns null if manager not available, otherwise true for legacy version, false for current version
 */
export function useIsLegacySchedulingUi(): boolean | null {
  const manager = useMfeVersionManager();

  if (!manager) {
    return null;
  }

  return isLegacySchedulingUiVersion(manager.getVersion('scheduling-ui'));
}

/**
 * Test-only export to reset the warned versions set between tests.
 * @internal
 */
export function __resetWarnedVersionsForTest__() {
  warnedUnparseableVersions.clear();
}
